<template>
  <div class="uk-grid-medium" uk-grid>
    <div class="uk-width-5-5">
      <div class="d-flex">
        <nav id="breadcrumbs" class="mb-3">
          <ul>
            <li>
              <router-link :to="{ name: 'company.dashboard' }">
                <i class="uil-home-alt"></i>
              </router-link>
            </li>
            <li> {{ $t('general.competency_and_user_def') }}</li>
          </ul>
        </nav>
      </div>

      <div class="section-header mb-lg-2 border-0 uk-flex-middle">
        <div class="section-header-left" />
        <div class="section-header-right">
          <button aria-expanded="false" class="btn btn-outline-primary" type="button" @click="openUserAssignmentModal">
            <i class="uil-user-check"></i> {{ $t('general.define_to_users') }}
          </button>

          <button v-if="$hasPermission('create_competency')" aria-expanded="false" class="btn btn-default" type="button"
            @click="openAddModal">
            <i class="uil-plus"></i> {{ $t('general.add_competency') }}
          </button>

          <button v-else aria-expanded="false" class="btn btn-default" type="button" disabled
            :style="{ cursor: 'not-allowed' }" :uk-tooltip="$t('general.no_permission')">
            <i class="uil-plus"></i> {{ $t('general.add_competency') }}
          </button>


        </div>
      </div>

      <template v-if="errorMessages">
        <div v-for="(error, index) in errorMessages" :key="index" class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p style="color: red">
            <i class="uil-exclamation-triangle"></i> {{ error }}
          </p>
        </div>
      </template>
      <div v-if="successMessage" class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: green">
          <i class="uil-exclamation-triangle"></i> {{ successMessage }}
        </p>
      </div>
      <div class="card">
        <!-- Card header -->
        <div class="card-header actions-toolbar border-0">
          <div class="d-flex justify-content-between">
            <div class="uk-search uk-search-navbar">
              <i class="uil-search position-absolute"></i>
              <input v-model="filter.search" :placeholder="$t('general.search')"
                class="uk-search-input shadow-0 uk-form-small" type="search" />
            </div>
            <div class="d-flex">
              <sort-dropdown :sort-options="sortOption" :title="sortTitle" @sort-selected="sortOrder" />
            </div>
          </div>
        </div>
        <!-- Table -->
        <div class="table-responsive">
          <div v-if="isLoading" class="text-center p-4">
            <default-loading />
          </div>
          <table v-else class="table align-items-center">
            <thead>
              <tr>
                <th scope="col"> {{ $t('general.competency_name') }}</th>
                <th scope="col"> {{ $t('general.description') }}</th>
                <th scope="col"> {{ $t('general.created_by') }}</th>
                <th scope="col"> {{ $t('excel.heading.content_count') }}</th>
                <th scope="col"> {{ $t('general.assignment_count') }}</th>
                <th scope="col">{{ $t("general.added_at") }}</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody class="list">
              <!--competency item-->
              <tr v-if="formattedItems.length === 0" class="text-center">
                <td class="py-4" colspan="7">
                  <div class="text-muted">
                    <i class="uil-inbox" style="font-size: 2rem;"></i>
                    <p class="mt-2"> {{ $t('general.no_have_competency') }} </p>
                  </div>
                </td>
              </tr>
              <tr v-for="competency in formattedItems" :key="competency.id">
                <td>{{ competency.name }}</td>
                <td>{{ competency.description }}</td>
                <td>{{ competency.created_by_name }}</td>
                <td>{{ competency.contents_count }}</td>
                <td>{{ competency.assignments_count }}</td>
                <td>{{ competency.created_at }}</td>
                <td class="text-right">
                  <div class="actions ml-3">
                    <button :uk-tooltip="$t('general.edit')" class="btn btn-icon btn-hover btn-sm btn-circle"
                      @click="openEditModal(competency)">
                      <i class="uil-edit "></i>
                    </button>
                    <button :uk-tooltip="$t('general.delete')" class="btn btn-icon btn-hover btn-sm btn-circle"
                      @click="deleteCompetency(competency.id)">
                      <i class="uil-minus-circle text-danger"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <pagination v-if="pagination && pagination.links && pagination.links.length > 0" :pagination="pagination"
        @changePage="setPage($event)"></pagination>

      <!-- Competency Modal -->
      <CompetencyAddUpdateModal ref="competencyModal" @success="onModalSuccess" />

      <!-- User Competency Assignment Modal - Lazy Loaded -->
      <UserCompetencyAssignmentModal v-if="showUserAssignmentModal" ref="userAssignmentModal"
        @success="onUserAssignmentSuccess" />

    </div>
  </div>
</template>

<script>
import store from "@/core/services";

import Pagination from "@/assets/components/Pagination.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";

import moment from "moment";
import competencies_module, {
  MODULE_NAME as COMPETENCIES_MODULE_NAME,
  BASE_URL,
  LOADING,
  ERROR,
  SUCCESS,
  ITEMS,
  GET_ITEMS,
  DELETE_ITEM,
  RESET_VALUES,
} from "@/core/services/store/competencies/competencies.module";

import _ from 'lodash';

import { confirmDialogDelete } from "@/utils/confirmDialog";
import { showSuccess, showError } from "@/utils/alertUtils";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";
import CompetencyAddUpdateModal from "../components/CompetencyAddUpdateModal.vue";
import UserCompetencyAssignmentModal from "../components/UserCompetencyAssignmentModal.vue";

const COMPETENCIES_MODULE = COMPETENCIES_MODULE_NAME;

export default {
  name: "CompetenciesAndUserDefinitions",
  components: { SortDropdown, DefaultLoading, Pagination, CompetencyAddUpdateModal, UserCompetencyAssignmentModal },
  data() {
    return {
      page: 1,
      per_page: 15,
      sort_by: "id",
      sort_desc: "desc",
      showUserAssignmentModal: false,
      filter: {
        search: null,
        order_by: "id",
        sort: "desc",
      },
      sortTitle: this.$t("filters.order.sort"),
      sortOption: [
        { key: 'text-asc', sort: 'asc', order: 'id', label: this.$t("sorting.a_z") },
        { key: 'text-desc', sort: 'desc', order: 'id', label: this.$t("sorting.z_a") },
        { key: 'created_at-asc', sort: 'asc', order: 'created_at', label: this.$t("sorting.created_at_asc") },
        { key: 'created_at-desc', sort: 'desc', order: 'created_at', label: this.$t("sorting.created_at_desc") },
      ],
    };
  },

  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(COMPETENCIES_MODULE, competencies_module);
  },
  computed: {
    isLoading() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + LOADING];
    },
    hasError() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + ERROR];
    },
    errorMessage() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + ERROR];
    },
    successMessage() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + SUCCESS];
    },
    allCompetencies() {
      return this.$store.getters[COMPETENCIES_MODULE + '/' + ITEMS];
    },
    pagination() {
      const items = this.$store.getters[COMPETENCIES_MODULE + '/' + ITEMS];
      return items?.meta || null;
    },
    formattedItems() {
      let results = [];
      let competencies = this.allCompetencies;

      // Handle different response structures
      if (competencies && competencies.data) {
        competencies = competencies.data;
      }

      if (!competencies || !Array.isArray(competencies)) return [];

      competencies.forEach((competency) => {
        results.push({
          ...competency,
          description: competency.description || '-',
          created_by_name: competency.created_by ?
            `${competency.created_by.name} ${competency.created_by.surname}` : '-',
          contents_count: competency.contents_count || 0,
          assignments_count: competency.assignments_count || 0,
          created_at: competency.created_at
            ? moment(competency.created_at).format("DD/MM/YYYY HH:mm:ss")
            : "-",
        });
      });

      return results;
    },
    errorMessages() {
      return this.hasError ? [this.errorMessage] : [];
    }
  },
  methods: {
    async fetchCompetencies(filters) {
      try {
        await this.$store.dispatch(COMPETENCIES_MODULE + '/' + GET_ITEMS, {
          filters: filters
        });
      } catch (error) {
        console.error('Fetch competencies error:', error);
        throw error;
      }
    },
    async deleteCompetency(id) {
      const isConfirmed = await confirmDialogDelete({ context: this });

      if (!isConfirmed) return;

      try {
        await this.$store.dispatch(COMPETENCIES_MODULE + '/' + DELETE_ITEM,
          { url: `${BASE_URL}/${id}` }
        );

        // Error'u temizle
        this.clearMessages();
        
        showSuccess(this.$t('general.successfully_deleted'), 3000);

        await this.getCompetenciesResult();
      } catch (error) {
        this.clearMessages();
        console.error("Delete error:", error);
        showError(this.$t(error), 8000);
      }
    },

    clearMessages() {
      this.$store.commit(COMPETENCIES_MODULE + '/' + RESET_VALUES);
    },
    async getCompetenciesResult() {
      try {
        const filters = {
          page: this.page,
        };

        if (this.filter.search) {
          filters.search = this.filter.search;
        }

        if (this.filter.order_by && this.filter.sort) {
          filters.order_by = this.filter.order_by;
          filters.sort = this.filter.sort;
        }

        await this.fetchCompetencies(filters);

        // Check if fetch was successful
        if (this.hasError) {
          showError(this.errorMessage || this.$t('general.error_occurred_while_loading_competencies'), 5000);
        }
      } catch (error) {
        console.error('Competencies fetch error:', error);
        showError(this.$t('general.error_occurred_while_loading_competencies'), 5000);
      }
    },

    sortOrder({ order, sort }) {
      this.page = 1;
      this.filter.order_by = order;
      this.filter.sort = sort;
      this.getCompetenciesResult();
    },
    setPage(page) {
      this.page = page;
      this.getCompetenciesResult();
    },
    resetMessages() {
      this.clearMessages();
    },

    openAddModal() {
      this.$nextTick(() => {
        this.$refs.competencyModal.openModal(null);
      });
    },

    openEditModal(competency) {
      this.$nextTick(() => {
        this.$refs.competencyModal.openModal(competency);
      });
    },

    onModalSuccess() {
      this.getCompetenciesResult();
    },

    async openUserAssignmentModal() {
      this.showUserAssignmentModal = true;
      await this.$nextTick();
      await this.$refs.userAssignmentModal.openModal();
    },

    onUserAssignmentSuccess() {
      // Kullanıcı atama başarılı olduğunda yapılacak işlemler
    },
  },
  mounted() {
    this.getCompetenciesResult();
  },
  beforeDestroy() {
    this.clearMessages();
  },
  watch: {
    "filter.search": function (val) {
      this.debouncedFilterSearch();
    },
    successMessage: function (val) {
      if (val) {
        this.debouncedResetValues();
      }
    },
    errorMessage: function (val) {
      if (val) {
        this.debouncedResetValues();
      }
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.getCompetenciesResult, 500);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>
<style scoped>
input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}
</style>
